import { Card } from '@ipfabric/ui-components/Card/Card';
import { PortalToRoot } from '@ipfabric/ui-components/PortalToRoot/PortalToRoot';
import type { RangeSeries } from '@ipfabric/utils/api/systemStatus';
import ChartJS from 'chart.js';
import { memo, useState } from 'react';
import { Line } from 'react-chartjs-2';

const PALETTE = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc948',
  '#b07aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ac',
  '#1f77b4',
  '#d62728',
  '#9467bd',
];

function serviceLabel(id: string): string {
  return (id.split('/').at(-1) ?? id).replace(/\.service$/, '');
}

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

type Props = {
  title: string;
  series: RangeSeries[] | null;
  formatY: (v: number) => string;
};

type LegendChart = { data: { datasets: Array<{ borderColor?: string }> } };
type LegendItem = { datasetIndex: number; [key: string]: unknown };

type TooltipModel = {
  opacity: number;
  x: number;
  y: number;
  title: string[];
  body: Array<{ lines: string[] }>;
  labelColors: Array<{ backgroundColor?: string }>;
};
type TooltipMe = { _chart: { canvas: HTMLCanvasElement } };

type ChartTooltipState = {
  left: number;
  top: number;
  title: string[];
  items: Array<{ text: string; color?: string }>;
};

export const ServiceRangeChart = memo(({ title, series, formatY }: Props) => {
  const hasData = series !== null && series.length > 0;
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);

  const chartData = hasData
    ? {
        labels: series[0]!.values.map(({ ts }) => formatTs(ts)),
        datasets: series.map((s, i) => ({
          label: serviceLabel(s.labels.id ?? ''),
          data: s.values.map(({ value }) => value),
          borderColor: PALETTE[i % PALETTE.length],
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          pointRadius: 0,
          pointHitRadius: 5,
        })),
      }
    : { labels: [], datasets: [] };

  const options = {
    animation: { duration: 0 },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            callback: (v: number) => formatY(v),
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            maxTicksLimit: 6,
            maxRotation: 0,
          },
        },
      ],
    },
    elements: { point: { radius: 0, hitRadius: 5 } },
    tooltips: {
      enabled: false,
      mode: 'index',
      intersect: false,
      filter: (item: { value: string }) => !Number.isNaN(Number(item.value)),
      itemSort: (a: { value: string }, b: { value: string }) =>
        Number(b.value) - Number(a.value),
      callbacks: {
        label: (
          item: { value: string; datasetIndex: number },
          data: { datasets: Array<{ label?: string }> },
        ) =>
          ` ${data.datasets[item.datasetIndex]?.label ?? ''}: ${formatY(Number(item.value))}`,
        labelColor: (item: { datasetIndex: number }, chart: LegendChart) => {
          const color =
            chart.data.datasets[item.datasetIndex]?.borderColor ??
            'transparent';
          return { borderColor: color, backgroundColor: color };
        },
      },
      custom: function (this: TooltipMe, tooltipModel: TooltipModel) {
        if (tooltipModel.opacity === 0) {
          setTooltip(null);
          return;
        }
        const canvasRect = this._chart.canvas.getBoundingClientRect();
        setTooltip({
          left: canvasRect.left + tooltipModel.x,
          top: canvasRect.top + tooltipModel.y,
          title: tooltipModel.title,
          items: tooltipModel.body.map((bodyItem, i) => ({
            text: bodyItem.lines[0] ?? '',
            color: tooltipModel.labelColors[i]?.backgroundColor,
          })),
        });
      },
    },
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        generateLabels: (chart: LegendChart) =>
          (
            ChartJS.defaults.global.legend.labels.generateLabels(
              chart,
            ) as LegendItem[]
          ).map((item) => ({
            ...item,
            pointStyle: 'rect',
            fillStyle: chart.data.datasets[item.datasetIndex]?.borderColor,
            lineWidth: 0,
          })),
      },
    },
  };

  return (
    <Card className='flex-1'>
      <div className='p text-level-5 font-semibold'>{title}</div>
      {hasData ? (
        <div
          className='px pb'
          style={{ height: 300 }}
          onMouseLeave={() => setTooltip(null)}
        >
          <Line data={chartData} options={options} />
        </div>
      ) : (
        <div className='p text-secondary'>No data</div>
      )}
      {tooltip && (
        <PortalToRoot>
          <div
            className='fixed z-tooltip pointer-events-none rounded-sm border border-solid border-colored-dark bg-colored-dark p-sm text-sm text-on-colored-dark shadow'
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            <div className='font-semibold'>{tooltip.title.join(', ')}</div>
            {tooltip.items.map((item, i) => (
              <div
                key={`${item.text}-${i}`}
                className='flex items-center gap-1'
              >
                <span
                  className='inline-block shrink-0'
                  style={{ width: 8, height: 8, backgroundColor: item.color }}
                />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </PortalToRoot>
      )}
    </Card>
  );
});
